/**
 * Read-only React Query hook for the platform workflow template
 * catalog (P4.T5).
 *
 * Fetches GET /api/workflows?is_template=true (P3.T9 backend) and
 * caches the result for 5 minutes — the registry rarely changes
 * mid-session.
 *
 * The WorkflowsCard and the WorkflowPickerModal both consume this
 * hook; they share a single RQ cache entry keyed by
 * `workflowsQueryKey`.
 *
 * Future tickets that add workspace-authored workflows (vs platform
 * templates) will need to widen this hook's query (drop the
 * `is_template=true` filter or add a workspace filter); the picker
 * will then surface both platform + workspace templates.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'

/** Mirrors `WorkflowBoundPersona` in `backend/app/schemas/workflows_api.py`
 * (P1.T4/T5) — personas currently bound to a workflow's engine config. */
export interface WorkflowBoundPersona {
  persona_id: string
  persona_name: string
  channel: string
}

/** Mirrors `WorkflowSummaryManifest` in backend/app/schemas/workflows_api.py
 * (SA-27) — the identity subset of the stored SA-15 template manifest.
 * `audience`/`trigger` are 'customer'|'internal' / 'inbound'|'scheduled'|
 * 'event' when well-formed, but the backend projection is defensive, so
 * they are typed as plain strings. */
/** Mirrors one `manifest.persona_blocks` entry (SA-31) — a recommended
 * persona-bundle starter block the Workshop offers at install time. */
export interface PersonaStarterBlock {
  ref: string
  category: string
  title: string
  content: string
}

export interface WorkflowSummaryManifest {
  audience: string | null
  trigger: string | null
  channels: string[]
  needs: { business_config: string[] }
  /** SA-30 forward-compat: null until template-scoped suites land. */
  scenario_count: number | null
  /** SA-31: full starter-block entries, declared order. Optional (not
   * `[]`-required) because the field is additive — treat absent as []. */
  persona_blocks?: PersonaStarterBlock[]
}

/** Mirrors the `forked_from` provenance dict written by
 * `POST /api/workflows/{id}/fork` (E1 ownership lanes, spec 2026-09-18 §4). */
export interface ForkedFrom {
  workflow_id: string
  workflow_name: string
  version_id: string
  version_number: number
  mode: 'take_ownership' | 'new_from_type'
  forked_at: string
  forked_by: string | null
}

/**
 * Mirrors the `_workflow_to_summary` projection in
 * `backend/app/routers/workflows.py:87-99`. The detail endpoint
 * (`GET /api/workflows/:id`) adds a `versions` field; the picker
 * doesn't need version detail beyond the `current_version_id`
 * pointer, so summary is enough.
 */
export interface WorkflowSummary {
  id: string
  name: string
  description: string | null
  languages: string[]
  is_template: boolean
  current_version_id: string | null
  required_capabilities: string[]   // NEW (P4.T9): surfaces via _workflow_to_summary
  created_at: string | null
  updated_at: string | null
  /** E1 (spec 2026-09-18 §5): `process` is the step-model kind. */
  kind: 'graph' | 'order' | 'process'
  source: 'platform' | 'custom'
  current_version_number: number | null
  bound_personas: WorkflowBoundPersona[]
  configured: boolean
  config_conflict: boolean
  /** SA-27: null for custom/pre-manifest rows. */
  manifest: WorkflowSummaryManifest | null
  /** Previously dropped by this type (wire drift fix). */
  archived_at: string | null
  /** E1 ownership lanes (spec 2026-09-18 §4.1): provenance of a forked
   * custom row; null for platform and in-app-created rows. */
  forked_from: ForkedFrom | null
  /** E1 viewer (spec 2026-09-18 §5): number of steps for `kind: process`
   * rows, null for every other kind. Optional because it is additive —
   * a summary without it renders a bare "Process" chip. */
  step_count?: number | null
  /** SA-469 (spec D5): a process row's `record.agenda` (its intake agenda); null or absent otherwise. */
  record_agenda?: string | null
  /** E3 draft lane (spec 2026-09-18-e3-e4 §4.1): a custom row carries an
   * unpublished author draft. Always false on platform rows; optional
   * because it is additive on the list wire (SA-396). */
  has_draft?: boolean
  draft_updated_at?: string | null
}

export const workflowsQueryKey = ['workflows', 'templates'] as const

export function useWorkflows(opts?: {
  enabled?: boolean
}): UseQueryResult<WorkflowSummary[], Error> {
  return useQuery<WorkflowSummary[], Error>({
    queryKey: workflowsQueryKey,
    queryFn: () =>
      apiClient.get<WorkflowSummary[]>('/api/workflows?is_template=true'),
    staleTime: 5 * 60_000,
    // The workflows router only registers under workflow_engine —
    // callers on deployments without it pass enabled:false so we never
    // fire a guaranteed-404 request.
    enabled: opts?.enabled ?? true,
  })
}

export const workflowsLibraryQueryKey = ['workflows', 'library'] as const

/** Tier-1 library: ALL workflows (templates + custom), enriched projection. */
export function useWorkflowsLibrary(): UseQueryResult<WorkflowSummary[], Error> {
  return useQuery<WorkflowSummary[], Error>({
    queryKey: workflowsLibraryQueryKey,
    queryFn: () => apiClient.get<WorkflowSummary[]>('/api/workflows'),
    staleTime: 60_000,
  })
}
